import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  CreditCard, RefreshCw, Users, TrendingUp, DollarSign, Activity, Coins, Zap,
} from 'lucide-react';
import DateWindowPicker from '../../components/DateWindowPicker';

/**
 * PaymentAnalyticsPage — iter 132.
 *
 * Shows how much money each payment method (CashApp, Apple Pay, Google Pay,
 * BTC, Chime, etc.) brought in over a selectable window. KPI = approved
 * deposits. Drill-down table exposes pending + rejected counts too so
 * admins can spot a method that's suddenly dumping lots of denials.
 */
export default function PaymentAnalyticsPage() {
  const [window, setWindow] = useState({ window: 'today' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (window.window) params.set('window', window.window);
      if (window.start) params.set('start', window.start);
      if (window.end) params.set('end', window.end);
      const r = await api.get(`/api/admin/payment-analytics?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load payment analytics');
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => {
    load();
    // Refresh every 30s while the page is open so the admin sees live
    // data without having to click refresh.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="admin-payment-analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-emerald-400" />
            Payment Method Analytics
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Live KPIs for every payment method — approved $, request volume, rejection rate.
            Updates every 30 s.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="border-white/10"
          data-testid="payment-analytics-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <DateWindowPicker value={window} onChange={setWindow} testidPrefix="payment-window" />

      {loading && !data ? (
        <div className="text-gray-400 text-sm py-10 text-center">Loading…</div>
      ) : data ? (
        <>
          {/* KPI row */}
          <div className="grid md:grid-cols-5 gap-3">
            <Kpi label="Approved $" value={`$${data.kpis.total_approved.toLocaleString()}`}
                 color="#22c55e" icon={<DollarSign className="w-4 h-4" />}
                 testid="kpi-total-approved" />
            <Kpi label="Requests" value={data.kpis.total_requests.toLocaleString()}
                 color="#6366f1" icon={<Activity className="w-4 h-4" />}
                 testid="kpi-total-requests" />
            <Kpi label="Unique Players" value={data.kpis.total_players.toLocaleString()}
                 color="#a855f7" icon={<Users className="w-4 h-4" />}
                 testid="kpi-total-players" />
            <Kpi label="Avg Deposit" value={`$${data.kpis.avg_deposit.toLocaleString()}`}
                 color="#F5D76E" icon={<TrendingUp className="w-4 h-4" />}
                 testid="kpi-avg-deposit" />
            <Kpi label="Top Method" value={data.kpis.top_method || '—'}
                 color="#38bdf8" icon={<CreditCard className="w-4 h-4" />}
                 testid="kpi-top-method" />
          </div>

          {/* Per-method table */}
          <div className="rounded-2xl p-5 space-y-3 border border-white/5"
               style={{ background: 'rgba(255,255,255,0.02)' }}
               data-testid="payment-method-table-section">
            <h2 className="font-bold text-white">By Payment Method</h2>
            {data.per_method.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center" data-testid="payment-method-empty">
                No deposits in this window.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                      <th className="text-left py-2 px-2">Method</th>
                      <th className="text-right py-2 px-2">Approved $</th>
                      <th className="text-right py-2 px-2">Approved #</th>
                      <th className="text-right py-2 px-2">Pending</th>
                      <th className="text-right py-2 px-2">Rejected</th>
                      <th className="text-right py-2 px-2">Failed</th>
                      <th className="text-right py-2 px-2">Players</th>
                      <th className="text-left py-2 px-2">Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_method.map((m) => {
                      const totalReq = m.approved_count + m.pending_count + m.rejected_count + m.failed_count;
                      const rejectPct = totalReq > 0 ? (m.rejected_count / totalReq) * 100 : 0;
                      const flag = rejectPct >= 25;
                      return (
                        <tr key={m.method}
                            className="border-b border-white/5 hover:bg-white/5"
                            data-testid={`payment-row-${m.method}`}>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              {m.logo_url && (
                                <img src={m.logo_url} alt="" className="w-6 h-6 rounded object-contain bg-black/30" />
                              )}
                              <span className="font-semibold text-white">{m.display_name}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-green-400 font-bold">
                            ${m.approved_sum.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-gray-300">{m.approved_count}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-amber-400">{m.pending_count}</td>
                          <td className={`py-2 px-2 text-right tabular-nums ${flag ? 'text-red-400 font-bold' : 'text-gray-400'}`}>
                            {m.rejected_count}{flag ? ` (${Math.round(rejectPct)}%)` : ''}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-gray-500">{m.failed_count}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-indigo-300">{m.unique_players}</td>
                          <td className="py-2 px-2 text-xs text-gray-500">{m.payment_mode}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Per-provider breakdown — iter 136. Compares Linkgen / NOWPayments
              / Manual / Receipt-based side-by-side with success rate so the
              admin can spot which provider is failing settlements. */}
          <div className="rounded-2xl p-5 space-y-3 border border-white/5"
               style={{ background: 'rgba(255,255,255,0.02)' }}
               data-testid="payment-provider-table-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> By Gateway Provider
              </h2>
              <span className="text-[11px] text-gray-500">Success rate = approved ÷ (approved + rejected + failed)</span>
            </div>
            {(!data.per_provider || data.per_provider.length === 0) ? (
              <p className="text-sm text-gray-400 py-6 text-center" data-testid="payment-provider-empty">
                No gateway deposits in this window.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                      <th className="text-left py-2 px-2">Provider</th>
                      <th className="text-right py-2 px-2">Approved $</th>
                      <th className="text-right py-2 px-2">Approved</th>
                      <th className="text-right py-2 px-2">Pending</th>
                      <th className="text-right py-2 px-2">Rejected</th>
                      <th className="text-right py-2 px-2">Failed</th>
                      <th className="text-right py-2 px-2">Success Rate</th>
                      <th className="text-right py-2 px-2">Players</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_provider.map((p) => {
                      const sr = p.success_rate;
                      const srColor = sr === null ? 'text-gray-500'
                        : sr >= 90 ? 'text-emerald-400 font-bold'
                        : sr >= 70 ? 'text-amber-400'
                        : 'text-red-400 font-bold';
                      const srBar = sr === null ? 0 : Math.max(0, Math.min(100, sr));
                      return (
                        <tr key={p.provider}
                            className="border-b border-white/5 hover:bg-white/5"
                            data-testid={`payment-provider-row-${p.provider}`}>
                          <td className="py-2 px-2 font-semibold text-white">{p.display_name}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-green-400 font-bold">
                            ${p.approved_sum.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-gray-300">{p.approved_count}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-amber-400">{p.pending_count}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-orange-400">{p.rejected_count}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-red-400">{p.failed_count}</td>
                          <td className={`py-2 px-2 text-right tabular-nums ${srColor}`}>
                            <div className="flex items-center justify-end gap-2">
                              <span>{sr === null ? '—' : `${sr}%`}</span>
                              {sr !== null && (
                                <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                  <div className="h-full transition-all" style={{
                                    width: `${srBar}%`,
                                    background: sr >= 90 ? '#22c55e' : sr >= 70 ? '#f59e0b' : '#ef4444',
                                  }} />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-indigo-300">{p.unique_players}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Per-coin crypto breakdown — iter 136. Pulled from NOWPayments
              IPN events; only renders when at least one crypto event exists
              in the window. Shows which coin volumes are flowing in. */}
          {data.per_coin && data.per_coin.length > 0 && (
            <div className="rounded-2xl p-5 space-y-3 border border-white/5"
                 style={{ background: 'rgba(255,255,255,0.02)' }}
                 data-testid="payment-coin-table-section">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Coins className="w-4 h-4 text-purple-400" /> Crypto Deposits — by Coin
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                      <th className="text-left py-2 px-2">Coin</th>
                      <th className="text-right py-2 px-2">Approved $</th>
                      <th className="text-right py-2 px-2">Approved</th>
                      <th className="text-right py-2 px-2">Pending</th>
                      <th className="text-right py-2 px-2">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_coin.map((c) => (
                      <tr key={c.coin}
                          className="border-b border-white/5 hover:bg-white/5"
                          data-testid={`payment-coin-row-${c.coin}`}>
                        <td className="py-2 px-2 font-semibold text-white">{c.display_name}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-green-400 font-bold">
                          ${c.approved_sum.toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">{c.approved_count}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-amber-400">{c.pending_count}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-red-400">{c.failed_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Day-by-day trend */}
          {data.by_day.length > 1 && (
            <div className="rounded-2xl p-5 border border-white/5"
                 style={{ background: 'rgba(255,255,255,0.02)' }}
                 data-testid="payment-trend-section">
              <h2 className="font-bold text-white mb-3">Daily Trend (approved $)</h2>
              <Sparkline points={data.by_day} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function Kpi({ label, value, color, icon, testid }) {
  return (
    <div
      className="rounded-xl p-3 border"
      style={{ background: '#131822', borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
    </div>
  );
}

function Sparkline({ points }) {
  const max = Math.max(...points.map(p => p.approved_sum), 1);
  return (
    <div className="flex items-end gap-1 h-32" data-testid="payment-sparkline">
      {points.map((p) => {
        const h = Math.max(6, (p.approved_sum / max) * 100);
        return (
          <div key={p.date} className="flex-1 flex flex-col items-center group relative">
            <div
              className="w-full rounded-t transition-all"
              style={{ height: `${h}%`, background: 'linear-gradient(to top, #22c55e, #86efac)' }}
            />
            <div className="text-[9px] text-gray-500 mt-1 truncate w-full text-center">
              {p.date.slice(5)}
            </div>
            <div className="absolute bottom-full mb-1 bg-black/90 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
              ${p.approved_sum.toLocaleString()} · {p.approved_count} deposits
            </div>
          </div>
        );
      })}
    </div>
  );
}
